import { cn } from "@/lib/utils";

interface SkeletonProps {
  className?: string;
}

export function Skeleton({ className }: SkeletonProps) {
  return (
    <div
      className={cn(
        "rounded-md bg-border/50 animate-shimmer",
        "bg-gradient-to-r from-border/50 via-border/20 to-border/50",
        "bg-[length:200%_100%]",
        className
      )}
    />
  );
}

export function SkeletonCard() {
  return (
    <div className="rounded-xl border border-border p-6 space-y-4">
      <Skeleton className="h-4 w-1/3" />
      <Skeleton className="h-8 w-2/3" />
      <Skeleton className="h-4 w-1/2" />
    </div>
  );
}

export function SkeletonRow() {
  return (
    <div className="flex items-center gap-4 py-3">
      <Skeleton className="h-10 w-10 rounded-full" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-1/3" />
        <Skeleton className="h-3 w-1/4" />
      </div>
      <Skeleton className="h-6 w-16 rounded-full" />
    </div>
  );
}

/**
 * SkeletonStatCard — placeholder for the 4-up KPI row that most
 * dashboards and overview pages open with. Matches StatCard's height
 * so swapping in the real component doesn't shift layout.
 */
export function SkeletonStatCard() {
  return (
    <div className="rounded-xl border border-border p-4 space-y-2">
      <Skeleton className="h-3 w-20" />
      <Skeleton className="h-8 w-24" />
      <Skeleton className="h-3 w-16" />
    </div>
  );
}

/**
 * SkeletonGrid — N cards in a responsive grid. Used by pages that show
 * a grid-of-cards on load (recruiting pipeline, performance reviews,
 * report tiles, etc). ``count`` defaults to 6 because that's what most
 * grids actually render above the fold.
 */
export function SkeletonGrid({
  count = 6,
  columns = "md:grid-cols-2 lg:grid-cols-3",
}: {
  count?: number;
  columns?: string;
}) {
  return (
    <div className={cn("grid grid-cols-1 gap-4", columns)}>
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonCard key={i} />
      ))}
    </div>
  );
}

/**
 * SkeletonDetailPage — header + two-panel layout for detail screens
 * (invoice, complaint, employee profile). Pages that need a finer
 * breakdown can still compose Skeletons directly; this covers the 80%
 * case.
 */
export function SkeletonDetailPage() {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-32" />
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-9 w-24" />
          <Skeleton className="h-9 w-24" />
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <SkeletonCard />
          <SkeletonCard />
        </div>
        <div className="space-y-4">
          <SkeletonCard />
        </div>
      </div>
    </div>
  );
}

/**
 * SkeletonCalendar — 6×7 month-grid placeholder. The day cells are
 * shaped like empty tiles so the real calendar swapping in doesn't
 * trigger a reflow from "tall skeleton" to "short month grid."
 */
export function SkeletonCalendar() {
  return (
    <div className="rounded-xl border border-border p-4 space-y-3">
      <div className="flex items-center justify-between">
        <Skeleton className="h-5 w-32" />
        <div className="flex gap-2">
          <Skeleton className="h-8 w-8 rounded-md" />
          <Skeleton className="h-8 w-8 rounded-md" />
        </div>
      </div>
      <div className="grid grid-cols-7 gap-2">
        {Array.from({ length: 7 }).map((_, i) => (
          <Skeleton key={`h-${i}`} className="h-3 w-full" />
        ))}
        {Array.from({ length: 42 }).map((_, i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    </div>
  );
}

/**
 * SkeletonOrgChart — tree of three ranks. Matches the actual OrgChart
 * layout closely enough that the swap looks intentional rather than
 * "the page just exploded into a tree."
 */
export function SkeletonOrgChart() {
  return (
    <div className="space-y-8 py-6">
      <div className="flex justify-center">
        <Skeleton className="h-20 w-56 rounded-xl" />
      </div>
      <div className="flex justify-center gap-4 flex-wrap">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-20 w-48 rounded-xl" />
        ))}
      </div>
      <div className="flex justify-center gap-3 flex-wrap">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-16 w-36 rounded-xl" />
        ))}
      </div>
    </div>
  );
}
